import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { baseURL, headers } from '@/v2/constants/requests'

const QUERY_KEY = ['SettingsBookingPages']
const LOGS_QUERY_KEY = ['SettingsBookingPageLogs']
const EMPTY_ARRAY = []

export const useSettingsBookingPages = () => {
  const { data, isLoading, isError } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const res = await fetch(`${baseURL}/v2/rest/settings/booking_pages`, {
        method: 'GET',
        headers,
      })
      if (!res.ok) {
        throw new Error("We couldn't load the booking pages")
      }
      return res.json()
    },
  })

  return { bookingPages: data ?? EMPTY_ARRAY, isLoading, isError }
}

export const useBookingPageLogs = (filters = {}) => {
  const { date, email } = filters

  const { data, isLoading, isError } = useQuery({
    queryKey: [...LOGS_QUERY_KEY, date, email],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (date) params.append('date', date)
      if (email) params.append('email', email)

      const queryString = params.toString()
      const url = `${baseURL}/v2/rest/settings/booking_pages/logs${
        queryString ? `?${queryString}` : ''
      }`

      const res = await fetch(url, {
        method: 'GET',
        headers,
      })
      if (!res.ok) {
        throw new Error("We couldn't load the booking page logs")
      }
      return res.json()
    },
  })

  return { logs: data ?? EMPTY_ARRAY, isLoading, isError }
}

export const useAddBookingPage = () => {
  const queryClient = useQueryClient()

  const { mutateAsync: addBookingPage, ...rest } = useMutation({
    mutationFn: async pageData => {
      const res = await fetch(`${baseURL}/v2/rest/settings/booking_pages`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ booking_page: pageData }),
      })
      if (!res.ok) {
        const error = await res.json()
        throw new Error(error.errors || 'Could not create booking page')
      }
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries(QUERY_KEY)
    },
  })

  return { addBookingPage, ...rest }
}

export const useUpdateBookingPage = () => {
  const queryClient = useQueryClient()

  const { mutateAsync: updateBookingPage, ...rest } = useMutation({
    mutationFn: async ({ id, ...pageData }) => {
      const res = await fetch(
        `${baseURL}/v2/rest/settings/booking_pages/${id}`,
        {
          method: 'PATCH',
          headers,
          body: JSON.stringify({ booking_page: pageData }),
        }
      )
      if (!res.ok) {
        const error = await res.json()
        throw new Error(error.errors || 'Could not update booking page')
      }
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries(QUERY_KEY)
    },
  })

  return { updateBookingPage, ...rest }
}

export const useDeleteBookingPage = () => {
  const queryClient = useQueryClient()

  const { mutateAsync: deleteBookingPage, ...rest } = useMutation({
    mutationFn: async id => {
      const res = await fetch(
        `${baseURL}/v2/rest/settings/booking_pages/${id}`,
        {
          method: 'DELETE',
          headers,
        }
      )
      if (!res.ok) {
        const error = await res.json()
        throw new Error(error.errors || 'Could not delete booking page')
      }
      return res.status === 204 ? {} : res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries(QUERY_KEY)
    },
  })

  return { deleteBookingPage, ...rest }
}
